(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.DIMTextModel = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, () => {
  'use strict';

  const SCHEMA_VERSION = 1;
  const RENDERER_VERSION = 1;
  const MAX_LAYERS = 3;
  const MAX_GRAPHEMES = 120;
  const LAYER_FIELDS = new Set([
    'id', 'text', 'fontId', 'fontRevision', 'x', 'y', 'size', 'rotation', 'curve', 'colour', 'align'
  ]);
  const CONTROL_CHARACTERS = /[\u0000-\u0009\u000B-\u001F\u007F\u200B\u200E\u200F\u202A-\u202E\u2066-\u2069]/u;
  const CURVE_CHARACTERS = /^[\p{Script=Latin}\p{M}0-9 .,!?'"’‘“”\-–—:;()&£€+/]*$/u;
  const SAFE_ID = /^[A-Za-z0-9_-]{1,64}$/;
  const SAFE_REVISION = /^(?:[A-Za-z0-9_.-]{1,128}|sha256:[a-f0-9]{64})$/i;

  const graphemes = (value) => {
    if (typeof value !== 'string') throw new TypeError('Text must be a string.');
    if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
      return [...new Intl.Segmenter('en-GB', { granularity: 'grapheme' }).segment(value)]
        .map((part) => part.segment);
    }
    if (!/^[\x20-\x7E\n]*$/.test(value)) {
      throw new Error('This browser needs a grapheme segmenter for these characters.');
    }
    return [...value];
  };

  const finiteInRange = (value, minimum, maximum, field) => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < minimum || value > maximum) {
      throw new TypeError(`Invalid ${field}.`);
    }
    return value;
  };

  const normaliseLineBreaks = (text) => text.replace(/\r\n?/g, '\n');
  const hasExplicitLineBreak = (text) => /[\r\n]/.test(text);
  const legacyCurveError = () => Object.assign(new Error('Curved text works with one line. Choose Straighten text or edit this text block to one line before Preview.'), { code: 'TEXT_MULTILINE_CURVE' });

  const validateLayers = (layers, { forExport = false, allowLegacyMultilineCurve = false } = {}) => {
    if (!Array.isArray(layers) || layers.length > MAX_LAYERS) {
      throw new Error('Use no more than three text blocks.');
    }
    const identifiers = new Set();
    return layers.map((input) => {
      if (!input || typeof input !== 'object' || Array.isArray(input)) throw new Error('Invalid text block.');
      for (const field of Object.keys(input)) {
        if (!LAYER_FIELDS.has(field)) throw new Error(`Unknown text field: ${field}.`);
      }
      if (typeof input.id !== 'string' || !SAFE_ID.test(input.id)) throw new Error('Invalid text ID.');
      if (identifiers.has(input.id)) throw new Error('Duplicate text ID.');
      identifiers.add(input.id);
      if (typeof input.text !== 'string' || input.text.length > 4096 || CONTROL_CHARACTERS.test(normaliseLineBreaks(input.text))) {
        throw new Error('Text contains an unsupported control character.');
      }
      const wording = normaliseLineBreaks(input.text);
      if (graphemes(wording).length > MAX_GRAPHEMES) {
        throw new Error('Use up to 120 characters per text block.');
      }
      if (wording.split('\n').length > 3) throw new Error('Use up to three lines of straight text.');
      if (forExport && !input.text.trim()) throw new Error('Remove empty text blocks before continuing.');
      if (typeof input.fontId !== 'string' || !SAFE_ID.test(input.fontId)) throw new Error('Unknown font.');
      if (typeof input.fontRevision !== 'string' || !SAFE_REVISION.test(input.fontRevision)) {
        throw new Error('Invalid font revision.');
      }
      if (typeof input.colour !== 'string' || !/^#[0-9a-f]{6}$/i.test(input.colour)) {
        throw new Error('Invalid colour.');
      }
      const align = input.align === undefined ? 'center' : input.align;
      if (!['left', 'center', 'right'].includes(align)) throw new Error('Invalid alignment.');
      const curve = finiteInRange(input.curve, -120, 120, 'curve');
      const retainedMultiline = curve && hasExplicitLineBreak(input.text) && allowLegacyMultilineCurve && !forExport;
      if (curve && hasExplicitLineBreak(input.text) && !retainedMultiline) {
        throw legacyCurveError();
      }
      if (curve && !CURVE_CHARACTERS.test(retainedMultiline ? wording.replace(/\n/g, '') : wording)) {
        throw new Error('Curved text supports Latin lettering only. Choose straight text for these characters.');
      }
      return {
        id: input.id,
        text: input.text,
        fontId: input.fontId,
        fontRevision: input.fontRevision,
        x: finiteInRange(input.x, -1, 2, 'x'),
        y: finiteInRange(input.y, -1, 2, 'y'),
        size: finiteInRange(input.size, 0.008, 0.25, 'size'),
        rotation: finiteInRange(input.rotation, -180, 180, 'rotation'),
        curve,
        colour: input.colour.toUpperCase(),
        align
      };
    });
  };

  const textState = (state) => {
    if (!state || typeof state !== 'object' || Array.isArray(state)) throw new Error('Invalid editor state.');
    const empty = state.textLayers === undefined || (Array.isArray(state.textLayers) && state.textLayers.length === 0);
    if (state.textSchemaVersion === undefined && empty) return [];
    if (state.textSchemaVersion !== SCHEMA_VERSION) throw new Error('Unsupported text schema.');
    if (state.textRendererVersion !== undefined && state.textRendererVersion !== RENDERER_VERSION) {
      throw new Error('This saved lettering version is not supported.');
    }
    // Reading/freezing a draft must preserve legacy lettering for explicit repair.
    // New output continues through strict validateLayers({ forExport: true }).
    return validateLayers(state.textLayers, { allowLegacyMultilineCurve: true });
  };

  const textKey = (state) => {
    const layers = textState(state);
    return JSON.stringify({ renderer: layers.length ? (state.textRendererVersion || RENDERER_VERSION) : 0, layers });
  };

  const editorStatesEqual = (left, right) => {
    if (!left || !right) return false;
    const close = (first, second) => Number.isFinite(Number(first)) && Number.isFinite(Number(second))
      && Math.abs(Number(first) - Number(second)) < 1e-6;
    try {
      return Number(left.outputWidth) === Number(right.outputWidth)
        && Number(left.outputHeight) === Number(right.outputHeight)
        && close(left.x, right.x)
        && close(left.y, right.y)
        && close(left.scale, right.scale)
        && Number(left.rotation) === Number(right.rotation)
        && textKey(left) === textKey(right);
    } catch {
      return false;
    }
  };

  const freezeEditorState = (state) => {
    const layers = Object.freeze(textState(state).map((item) => Object.freeze({ ...item })));
    return Object.freeze({
      ...state,
      textSchemaVersion: SCHEMA_VERSION,
      textRendererVersion: RENDERER_VERSION,
      textLayers: layers
    });
  };

  return Object.freeze({
    SCHEMA_VERSION,
    RENDERER_VERSION,
    MAX_LAYERS,
    MAX_GRAPHEMES,
    graphemes,
    normaliseLineBreaks,
    hasExplicitLineBreak,
    legacyCurveError,
    validateLayers,
    textState,
    textKey,
    editorStatesEqual,
    freezeEditorState
  });
}));
